import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Steps, Button, message, Space } from 'antd';
import { DealsApi } from 'api/api';
import {
  CreateDealCommand,
  CreateDealPaymentCommand,
  CreateUpdateDealUnitCommand,
  DealPricingMode,
  DealStage,
} from 'api/models';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import ClientSelection from '../components/DealSelectionSteps/ClientSelection';
import PaymentPlan from '../components/DealSelectionSteps/PaymentPlan';
import ProjectAndUnitSelection from './DealSelectionSteps/ProjectAndUnitSelection';

const dealsApi = new DealsApi();

const DealFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateDealCommand>>({});

  const steps = [
    {
      title: t('deals:select-client', 'Select Client'),
      content: (
        <ClientSelection
          value={formData.clientId}
          onChange={clientId => setFormData(prev => ({ ...prev, clientId }))}
        />
      ),
    },
    {
      title: t('deals:select-details', 'Deal Details'),
      content: (
        <ProjectAndUnitSelection
          value={{
            projectId: formData.projectId,
            units: formData.units as CreateUpdateDealUnitCommand[],
            stage: formData.stage as DealStage,
            pricingMode: formData.pricingMode as DealPricingMode,
          }}
          onChange={({ projectId, units, stage, pricingMode }) => {
            console.log(units);
            setFormData(prev => ({ ...prev, projectId, units, stage, pricingMode }));
          }}
        />
      ),
    },
  ];

  // Add payment plan step only if stage is ClosedWon
  if (formData.stage === DealStage.ClosedWon) {
    steps.push({
      title: t('deals:payment-plan', 'Payment Plan'),
      content: (
        <PaymentPlan
          units={formData.units as CreateUpdateDealUnitCommand[]}
          value={formData.payments as CreateDealPaymentCommand[]}
          onChange={payments => setFormData(prev => ({ ...prev, payments }))}
        />
      ),
    });
  }

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const validUnits = formData.units?.filter(unit => unit.quantity && unit.quantity > 0);
      await dealsApi.apiDealsPost({
        createDealCommand: {
          ...formData,
          units: validUnits,
        } as CreateDealCommand,
      });
      message.success(t('deals:created', 'Deal created successfully'));
      navigate('/deals');
    } catch (error) {
      message.error(t('deals:create-error', 'Failed to create deal'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/deals');
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0:
        return !!formData.clientId;
      case 1:
        return formData.projectId && formData.units!.length! > 0 && formData.stage != undefined;
      case 2:
        if (formData.stage === DealStage.ClosedWon) {
          const totalUnitsAmount =
            formData.units?.reduce(
              (total, unit) => total + unit.quantity! * unit.negotiatedPricePerSqm! * unit.squareMeters!,
              0,
            ) || 0;
          const totalPayments = formData.payments?.reduce((total, payment) => total + payment.amount!, 0) || 0;
          return totalUnitsAmount === totalPayments;
        }
        return true;
      default:
        return true;
    }
  };

  return (
    <>
      <PageHeader
        ghost
        title={t('deals:create-new', 'Create New Deal')}
        subTitle={t('deals:follow-steps', 'Follow the steps to create a new deal')}
      />

      <Main>
        <Card>
          <Steps current={currentStep} items={steps} className="mb-8" />

          <div className="min-h-[400px] mb-8 mt-5">{steps[currentStep].content}</div>

          <div className="border-t pt-4 mt-8">
            <div className="flex justify-between items-center" style={{ display: 'flex', float: 'right', gap: '10px' }}>
              <Space>
                <Button onClick={handleCancel}>{t('global:cancel', 'Cancel')}</Button>
                {currentStep > 0 && <Button onClick={handleBack}>{t('global:back', 'Back')}</Button>}
              </Space>

              <Button
                type="primary"
                onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext}
                loading={loading}
                disabled={!validateStep()}
              >
                {currentStep === steps.length - 1 ? t('global:create', 'Create') : t('global:next', 'Next')}
              </Button>
            </div>
          </div>
        </Card>
      </Main>
    </>
  );
};

export default DealFormPage;
